import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Software, SoftwareFormData } from '@/types/software';
import { toast } from '@/hooks/use-toast';
import { useClientFilter } from '@/hooks/useClientFilter';
import { useAuth } from '@/hooks/useAuth';

export const useSoftwares = () => {
  const { getClientFilter, clientLoading, isAdmin } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['softwares', clientId],
    queryFn: async () => {
      let query = supabase
        .from('cmdb_softwares')
        .select(`
          *,
          fabricante:fabricantes(id, nome),
          fornecedor:fornecedores(id, nome),
          contrato:contratos(id, numero_contrato, nome_contrato)
        `)
        .order('nome');

      // Admins veem todos, outros usuários veem apenas do seu cliente
      if (!isAdmin && clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Buscar contagem de licenças para cada software
      const softwaresWithCounts = await Promise.all(
        (data || []).map(async (software) => {
          const { count: totalLicencas } = await supabase
            .from('cmdb_software_licencas')
            .select('*', { count: 'exact', head: true })
            .eq('software_id', software.id);

          const { count: licencasEmUso } = await supabase
            .from('cmdb_software_licencas')
            .select('*', { count: 'exact', head: true })
            .eq('software_id', software.id)
            .eq('status', 'em_uso');

          return {
            ...software,
            licencas_em_uso: licencasEmUso || 0,
            licencas_disponiveis: (totalLicencas || 0) - (licencasEmUso || 0),
          } as Software;
        })
      );

      return softwaresWithCounts;
    },
    enabled: !clientLoading,
  });
};

export const useSoftware = (id?: string) => {
  const { getClientFilter } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['software', id, clientId],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('cmdb_softwares')
        .select(`
          *,
          fabricante:fabricantes(id, nome),
          fornecedor:fornecedores(id, nome),
          contrato:contratos(id, numero_contrato, nome_contrato)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Software;
    },
    enabled: !!id,
  });
};

export const useCreateSoftware = () => {
  const queryClient = useQueryClient();
  const { getClientFilter } = useClientFilter();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: SoftwareFormData) => {
      const clientId = getClientFilter();
      
      const { data: result, error } = await supabase
        .from('cmdb_softwares')
        .insert({
          ...data,
          client_id: data.client_id || clientId,
          criado_por: user?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Software cadastrado',
        description: 'O software foi cadastrado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao cadastrar software',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateSoftware = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SoftwareFormData> }) => {
      const { data: result, error } = await supabase
        .from('cmdb_softwares')
        .update({
          ...data,
          atualizado_por: user?.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      queryClient.invalidateQueries({ queryKey: ['software'] });
      toast({
        title: 'Software atualizado',
        description: 'O software foi atualizado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar software',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteSoftware = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cmdb_softwares')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['softwares'] });
      toast({
        title: 'Software excluído',
        description: 'O software foi excluído com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir software',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
